import { DateTime } from 'luxon';
import { getTimeZones } from '@vvo/tzdb';

/**
 * Gets the current timezone of the user's browser
 * @returns string representing the current timezone
 */
export const getCurrentTimezone = (): string => {
  return DateTime.local().zoneName;
};

/**
 * Formats a timezone for display
 * @param timezone - Timezone string
 * @returns Formatted timezone string
 */
export const formatTimezone = (timezone: string): string => {
  const zones = getTimeZones();
  const zone = findTimezoneInGroups(timezone, zones);
  if (!zone) return timezone;

  const offsetMinutes = zone.currentTimeOffsetInMinutes;
  const hours = Math.abs(Math.floor(offsetMinutes / 60));
  const minutes = Math.abs(offsetMinutes % 60);
  const sign = offsetMinutes >= 0 ? '+' : '-';

  return `(GMT${sign}${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}) ${zone.alternativeName} - ${zone.mainCities[0]}`;
};

/**
 * Finds a timezone in the available zones, considering groups
 * @param timezone - Timezone string to find
 * @param zones - Array of timezone objects (optional, will fetch if not provided)
 * @returns The found timezone object or undefined
 */
export const findTimezoneInGroups = (
  timezone: string,
  zones?: ReturnType<typeof getTimeZones>,
) => {
  const allZones = zones || getTimeZones();
  return allZones.find(
    (zone) => zone.name === timezone || zone.group.includes(timezone),
  );
};

/**
 * Gets the canonical timezone name (considering groups)
 * @param timezone - Timezone string
 * @returns The canonical timezone name
 */
export const getCanonicalTimezone = (timezone: string): string => {
  const zone = findTimezoneInGroups(timezone);
  return zone?.name || timezone;
};
